/**
 * Shared video-tier selection for the scroll-scrub backdrops (the House on
 * /new, the Forest on /buy). Full masters are ~1080p and heavy; each has a
 * ~720p mobile tier that is retina-sharp on a phone and starts fast on
 * cellular. This module decides which to serve.
 */

/** Which tier to serve. Touch devices (phones, tablets) get the mobile tier —
 *  720p is already retina at that screen size, and the light master starts
 *  fast and spares cellular data. A coarse primary pointer is the reliable
 *  phone/tablet signal; raw dimensions are not, because a normal landscape
 *  laptop (e.g. 1440×900) has a small *height* and would be misread as mobile.
 *  Desktops (fine pointer) get the full master unless the connection asks
 *  otherwise (Data Saver, a 2g/3g estimate, or a low measured downlink), or
 *  the viewport is unambiguously phone-narrow. Pure so it can be unit-tested. */
export function videoTierFor(o: {
  minViewport: number;
  coarsePointer?: boolean;
  saveData?: boolean | undefined;
  effectiveType?: string | undefined;
  downlink?: number | undefined;
}): 'full' | 'mobile' {
  const slow = !!o.effectiveType && /(slow-2g|2g|3g)/.test(o.effectiveType);
  const lowBandwidth = typeof o.downlink === 'number' && o.downlink > 0 && o.downlink < 3;
  const phoneNarrow = o.minViewport <= 540; // no desktop window is this narrow
  return o.coarsePointer === true || phoneNarrow || o.saveData === true || slow || lowBandwidth
    ? 'mobile'
    : 'full';
}

/** Read the live environment and resolve the tier. SSR-safe (returns 'full'
 *  when there's no window). */
export function currentVideoTier(): 'full' | 'mobile' {
  if (typeof window === 'undefined') return 'full';
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string; downlink?: number };
  }).connection;
  return videoTierFor({
    minViewport: Math.min(window.innerWidth, window.innerHeight),
    coarsePointer: window.matchMedia('(pointer: coarse)').matches,
    saveData: conn?.saveData,
    effectiveType: conn?.effectiveType,
    downlink: conn?.downlink,
  });
}
